import React, { useState } from 'react';
import { PaperWrapper, Title } from '../common/styled';
import { Box, CircularProgress } from '@mui/material';
import { SearchInputWrapper, StyledButton } from './styled';
import TextInputWithIcon from '../../components/TextInputWithIcon';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import StudentTransportReportTable from './components/StudentTransportReportTable';
import { StudentStopsReport } from './types';
import report, { StudentStopsReportRequestBody } from '../../apis/report';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { ButtonContainer } from '../Staff/styled';
import SelectAll from '../../components/SelectAllField/SelectAllField';

const StudentTransportReportPage = () => {
  const [studentName, setStudentName] = useState<string>('');
  const [grades, setGrades] = useState<string[]>([]);
  const [divisions, setDivisions] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [studentStopsReport, setStudentStopsReport] = useState<
    StudentStopsReport[]
  >([]);

  const { divisionOptions, gradeOptions } = useSchoolClassesConfigHook();

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const getFilter = ({
    studentName,
    grades,
    divisions
  }: StudentStopsReportRequestBody) => {
    const filter: Record<string, any> = {};
    if (studentName && studentName.trim() !== '') {
      filter.studentName = studentName;
    }

    if (Array.isArray(grades) && grades.length > 0) {
      filter.grades = grades.map(String);
    }

    if (Array.isArray(divisions) && divisions.length > 0) {
      filter.divisions = divisions.map(String);
    }

    return filter;
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const handleSearch = async () => {
    setIsSearching(true);
    setHasSearched(false);
    setStudentStopsReport([]);
    try {
      const filter = getFilter({ studentName, grades, divisions });
      const response = await report.fetchStudentTransportReport(
        loggedInUser.school,
        filter
      );
      setStudentStopsReport(response as any);
      showToast(
        'Successfully fetched student transport report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error in fetching student transport report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  return (
    <>
      <Title>All Students</Title>
      <Box>
        <PaperWrapper>
          <SearchInputWrapper>
            <Box sx={{ maxWidth: '200px' }}>
              <TextInputWithIcon
                fullWidth
                label='Student Name'
                placeholder='Search by Student Name'
                value={studentName}
                onChange={(e) => {
                  setHasSearched(false);
                  setStudentName(e.target.value);
                }}
              />
            </Box>
            <Box sx={{ width: '200px', maxWidth: '200px' }}>
              <SelectAll
                label='Grade'
                placeholder='Select Grade'
                selected={grades}
                options={gradeOptions}
                onChange={(e) => {
                  setHasSearched(false);
                  setGrades(e);
                }}
              />
            </Box>
            <Box sx={{ width: '200px', maxWidth: '200px' }}>
              <SelectAll
                label='Division'
                placeholder='Select Division'
                selected={divisions}
                options={divisionOptions}
                onChange={(e) => {
                  setHasSearched(false);
                  setDivisions(e);
                }}
              />
            </Box>
            <ButtonContainer>
              <StyledButton onClick={handleSearch}>
                {isSearching ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Search'
                )}
              </StyledButton>
            </ButtonContainer>
          </SearchInputWrapper>
        </PaperWrapper>
      </Box>

      {hasSearched && (
        <StudentTransportReportTable
          studentTransportData={studentStopsReport}
        />
      )}

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default StudentTransportReportPage;
